import { useEffect, useRef, useState } from "react";

// 시안 v6 의 SVG 차트(svgLine·svgBars)를 React 로 옮긴 것. 칸 너비(px)를 viewBox 너비로 써서 글자 크기가 화면과 1:1이다.
// 툴팁은 SVG <title>(브라우저 기본 말풍선)로 단순화했다.
export function niceStep(max, dec = false) {
    const raw = dec ? max / 4 : Math.max(1, max / 4);
    const p = 10 ** Math.floor(Math.log10(raw));
    const r = raw / p;
    const s = (r <= 1 ? 1 : r <= 2 ? 2 : r <= 5 ? 5 : 10) * p;
    return dec ? +s.toPrecision(2) : Math.max(1, s);
}

const nf = (v) => Number(v).toLocaleString("ko-KR");

function useWidth(fixed) {
    const ref = useRef(null);
    const [w, setW] = useState(fixed || 900);
    useEffect(() => {
        if (fixed || !ref.current || typeof ResizeObserver === "undefined") return undefined;
        const ro = new ResizeObserver(([e]) => setW(Math.max(260, Math.round(e.contentRect.width))));
        ro.observe(ref.current);
        return () => ro.disconnect();
    }, [fixed]);
    return [ref, fixed || w];
}

const L = 48, R = 18, T = 14, B = 30;
const tipText = (label, name, v, fmt) => `${label} · ${name || "값"} ${fmt ? fmt(v) : nf(v)}`;

export function LineChart({ values, labels, name, color = "#2563eb", height = 230, width, fmt }) {
    const [ref, W] = useWidth(width);
    const H = height, n = values.length;
    const max = Math.max(1, ...values);
    const step = niceStep(max);
    const top = Math.ceil(max / step) * step;
    const x = (i) => L + (n === 1 ? 0 : (i * (W - L - R)) / (n - 1));
    const y = (v) => T + (H - T - B) * (1 - v / top);
    const grid = [];
    for (let v = 0; v <= top; v += step) grid.push(v);
    const every = Math.max(1, Math.round(n / Math.max(4, Math.floor(W / 140))));
    const show = (i) => i === n - 1 || (i % every === 0 && i < n - 1 - every / 2);
    const area = n ? `M${x(0)},${y(0)} ${values.map((v, i) => `L${x(i)},${y(v)}`).join(" ")} L${x(n - 1)},${y(0)} Z` : "";
    return (
        <div ref={ref} className="cslot" style={{ minHeight: H }}>
            <svg className="chart" viewBox={`0 0 ${W} ${H}`} width={W} height={H}>
                {grid.map((v) => (
                    <g key={v}><line x1={L} x2={W - R} y1={y(v)} y2={y(v)} stroke="#eef1f5" />
                        <text x={L - 8} y={y(v) + 4} textAnchor="end" fontSize="11" fill="#9ca3af">{fmt ? fmt(v) : v}</text></g>
                ))}
                {n > 0 && <path d={area} fill={color} opacity=".08" />}
                <polyline points={values.map((v, i) => `${x(i)},${y(v)}`).join(" ")} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" />
                {values.map((v, i) => (
                    <g key={i} className="g"><title>{tipText(labels[i], name, v, fmt)}</title>
                        <circle className="dot" cx={x(i)} cy={y(v)} r="3" fill="#fff" stroke={color} strokeWidth="2" /></g>
                ))}
                {labels.map((l, i) => show(i) && (
                    <text key={i} x={x(i)} y={H - 8} textAnchor={i === 0 ? "start" : i === n - 1 ? "end" : "middle"} fontSize="11" fill="#6b7280">{l}</text>
                ))}
            </svg>
        </div>
    );
}

export function BarChart({ values, labels, name, color = "#2563eb", height = 220, width, fmt, dec = false, onPick }) {
    const [ref, W] = useWidth(width);
    const H = height, n = values.length;
    const max = Math.max(dec ? 1e-6 : 1, ...values);
    const step = niceStep(max, dec);
    const top = +(Math.ceil(max / step - 1e-9) * step).toPrecision(6);
    const bw = (W - L - R) / Math.max(1, n);
    const y = (v) => T + (H - T - B) * (1 - v / top);
    const grid = [];
    for (let v = 0; v <= top + 1e-9; v = +(v + step).toPrecision(6)) grid.push(+v.toPrecision(4));
    const every = Math.max(1, Math.ceil(n / Math.max(4, Math.floor(W / 70))));
    return (
        <div ref={ref} className="cslot" style={{ minHeight: H }}>
            <svg className="chart" viewBox={`0 0 ${W} ${H}`} width={W} height={H}>
                {grid.map((v) => (
                    <g key={v}><line x1={L} x2={W - R} y1={y(v)} y2={y(v)} stroke="#eef1f5" />
                        <text x={L - 8} y={y(v) + 4} textAnchor="end" fontSize="11" fill="#9ca3af">{fmt ? fmt(v) : v}</text></g>
                ))}
                {values.map((v, i) => (
                    <rect key={i} className="bar" x={L + i * bw + bw * 0.2} y={Math.min(y(v), y(0) - 1)} width={bw * 0.6}
                        height={Math.max(1, y(0) - y(v))} rx="3" fill={color} style={onPick ? { cursor: "pointer" } : undefined}
                        onClick={onPick ? () => onPick(i) : undefined}>
                        <title>{tipText(labels[i], name, v, fmt)}</title>
                    </rect>
                ))}
                {labels.map((l, i) => i % every === 0 && (
                    <text key={i} x={L + i * bw + bw / 2} y={H - 8} textAnchor="middle" fontSize="11" fill="#6b7280">{l}</text>
                ))}
            </svg>
        </div>
    );
}
